/**
 * Where the API lives.
 *
 * Read once, from the build-time environment, and validated here rather than
 * at every call site. `VITE_API_BASE_URL` is compiled into the bundle and is
 * fully visible to anyone who opens the page - it is a location, not a secret,
 * and nothing secret may ever be added to a `VITE_*` variable.
 */

function readBaseUrl(): string {
  const raw = import.meta.env.VITE_API_BASE_URL;
  if (typeof raw !== "string" || raw.trim() === "") {
    // Same-origin. Correct when the built bundle is served by a reverse proxy
    // in front of the API, which is the deployment that needs no CORS at all.
    return "";
  }
  const trimmed = raw.trim().replace(/\/+$/, "");
  let parsed: URL;
  try {
    parsed = new URL(trimmed);
  } catch {
    throw new Error(
      "VITE_API_BASE_URL geçerli bir adres değil. Örnek: http://localhost:8000",
    );
  }
  if (parsed.protocol !== "http:" && parsed.protocol !== "https:") {
    throw new Error("VITE_API_BASE_URL yalnızca http veya https olabilir.");
  }
  // A cross-origin API cannot carry the session.
  //
  // This is not a style preference, it is how browsers behave: the session is
  // an HttpOnly cookie, and on a cross-origin request the browser neither
  // stores the `Set-Cookie` that login returns nor sends the cookie back. The
  // symptom is brutal to diagnose from the outside - login answers 200, and
  // the very next `/auth/me` answers 401, forever.
  //
  // That is exactly what a stale `VITE_API_BASE_URL=http://localhost:8000`
  // produced in a real browser once the API grew sessions. So it fails here,
  // loudly, with the fix in the message, rather than silently in a loop.
  //
  // Development is meant to go through the Vite proxy (see vite.config.ts) and
  // production behind one reverse proxy: both make this variable unnecessary.
  if (typeof window !== "undefined" && parsed.origin !== window.location.origin) {
    throw new Error(
      `VITE_API_BASE_URL (${parsed.origin}) sayfanın kaynağından (${window.location.origin}) farklı. ` +
        "Oturum çerezi farklı kaynaklar arasında taşınmaz; giriş yapılsa bile oturum açılmaz. " +
        "apps/web/.env.local dosyasındaki VITE_API_BASE_URL satırını kaldırın: geliştirme sunucusu API'yi kendisi vekiller.",
    );
  }
  return trimmed;
}

export const API_BASE_URL = readBaseUrl();

/**
 * Build a request URL from a path this code wrote.
 *
 * The path is always a literal from `client.ts` with interpolated ids - it is
 * never a URL that arrived from the API. That restriction is the whole point:
 * a backend field treated as a URL is how a service ends up fetching, or
 * navigating to, wherever its own data tells it to.
 */
export function apiUrl(path: string, query?: Record<string, string | number | undefined>): string {
  if (!path.startsWith("/")) throw new Error(`API path must be absolute: ${path}`);
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(query ?? {})) {
    if (value !== undefined) search.set(key, String(value));
  }
  const qs = search.toString();
  return `${API_BASE_URL}${path}${qs ? `?${qs}` : ""}`;
}
